import { Component } from '@theme/component';

/**
 * Custom element for slideshow swiper controls with progress bars
 * @extends {Component}
 */
export class SlideshowSwiperControls extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();

    // Find the parent slideshow component
    this.slideshow = this.closest('slideshow-component');
    if (!this.slideshow) return;

    // Get all progress items
    this.progressItems = Array.from(
      this.querySelectorAll('.slideshow-swiper-progress-item')
    );
    this.progressBars = Array.from(
      this.querySelectorAll('.slideshow-swiper-progress-bar')
    );

    // Add click handlers to progress items
    this.progressItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.slideshow.select(index);
      });
    });

    // Listen for slide changes
    this.slideshow.addEventListener('slideshow:select', this.handleSlideChange);

    // Initialize progress bars
    const initialIndex = this.slideshow.current || 0;
    this.updateProgress(initialIndex);

    // Handle autoplay progress animation
    if (this.slideshow.autoplay) {
      this.setupAutoplayProgress();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.slideshow) {
      this.slideshow.removeEventListener(
        'slideshow:select',
        this.handleSlideChange
      );
    }

    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    if (this.progressAnimation) {
      cancelAnimationFrame(this.progressAnimation);
      this.progressAnimation = null;
    }
  }

  handleSlideChange = (event) => {
    const { index } = event.detail;
    this.updateProgress(index);
  };

  updateProgress(activeIndex) {
    // Cancel any existing animation
    if (this.progressAnimation) {
      cancelAnimationFrame(this.progressAnimation);
      this.progressAnimation = null;
    }

    // Update active state
    this.progressItems.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.setAttribute('data-active', isActive);

      // Reset progress bar
      const progressBar = item.querySelector('.slideshow-swiper-progress-bar');
      if (progressBar) {
        progressBar.style.width = isActive ? '0%' : '0%';
      }
    });

    // Start progress animation for active slide if autoplay is enabled
    if (this.slideshow?.autoplay) {
      // Small delay to ensure slide transition is complete
      setTimeout(() => {
        this.startProgressAnimation(activeIndex);
      }, 100);
    }
  }

  startProgressAnimation(index) {
    // Cancel any existing animation
    if (this.progressAnimation) {
      cancelAnimationFrame(this.progressAnimation);
      this.progressAnimation = null;
    }

    const activeItem = this.progressItems[index];
    if (!activeItem) return;

    const progressBar = activeItem.querySelector(
      '.slideshow-swiper-progress-bar'
    );
    if (!progressBar) return;

    // Get autoplay interval
    const autoplayInterval = this.slideshow.autoplayInterval || 4000;

    // Reset progress
    progressBar.style.width = '0%';

    // Animate progress bar
    const startTime = performance.now();
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min((elapsed / autoplayInterval) * 100, 100);

      progressBar.style.width = `${progress}%`;

      if (progress < 100) {
        this.progressAnimation = requestAnimationFrame(animate);
      } else {
        this.progressAnimation = null;
      }
    };

    this.progressAnimation = requestAnimationFrame(animate);
  }
}

if (!customElements.get('slideshow-swiper-controls')) {
  customElements.define('slideshow-swiper-controls', SlideshowSwiperControls);
}

